/**
 * The Fibonacci module: retracements drawn on swings.
 *
 * This is the strategy's main module, and the one every other signal is
 * weighed against (see MODULE_IMPORTANCE). It works on exactly three
 * timeframes rather than offering all of them: the swings worth drawing on
 * live on M1, M15, H1, H4 and D1, and letting the module answer for a timeframe the
 * strategy does not use would present noise as a choice.
 *
 * The first half of this file is the shape of a swing and the arithmetic of
 * the levels on it; the second half is the detection, written as the rules
 * were taught (each rule in a doc comment next to the code that applies it).
 */
export const FIB_TIMEFRAMES = ['M1', 'M15', 'H1', 'H4', 'D1'];
/**
 * How many closed bars the module loads per timeframe.
 *
 * Enough history for a swing to be found well behind the current price, but
 * bounded: a retracement is measured against recent structure, and D1 at 600
 * bars is already well over two years of sessions.
 */
export const FIB_SCAN_BARS = {
    // Deep on purpose: the owner draws teaching examples on history, so the
    // chart has to reach well back. Roughly: M1 three weeks, M15 nine months,
    // H1 three years, H4 six years, D1 everything there is.
    M1: 20000,
    M15: 15000,
    H1: 15000,
    H4: 8000,
    D1: 5000,
};
/** Bars visible when a chart opens, so the bodies stay readable. */
export const FIB_VISIBLE_BARS = {
    M1: 240,
    M15: 220,
    H1: 220,
    H4: 200,
    D1: 160,
};
/**
 * The retracement ratios drawn on a swing.
 *
 * The standard set, as a starting point. 0 sits at the swing's end and 1 at
 * its start, so a retracement is read as how far back toward the start price
 * has come. Which of these the strategy actually trades is a rule for later;
 * the chart draws all of them until then.
 *
 * 0.118, 0.441, 0.559 and 0.702 are the owner's additions: the midpoints
 * of 0–0.236, 0.382–0.5, 0.5–0.618 and 0.618–0.786.
 */
export const FIB_RATIOS = [0, 0.118, 0.236, 0.382, 0.441, 0.5, 0.559, 0.618, 0.702, 0.786, 1];
export function swingRange(swing) {
    return Math.abs(swing.end.price - swing.start.price);
}
/**
 * Retracement levels for a swing.
 *
 * Ratio 0 is the swing's end and 1 its start, so on an up swing the levels
 * descend from the high and on a down swing they rise from the low — the way
 * every charting tool draws the retracement.
 */
export function fibLevels(swing, ratios = FIB_RATIOS) {
    const sign = swing.direction === 'up' ? -1 : 1;
    const range = swingRange(swing);
    return ratios.map((ratio) => ({ ratio, price: swing.end.price + sign * ratio * range }));
}
/**
 * How far `price` has retraced the swing, as a ratio of its range.
 *
 * 0 at the swing's end, 1 back at its start. Negative means price has gone
 * beyond the end (the swing is extending, not retracing); above 1 means the
 * start has been taken out and the swing is broken.
 */
export function retracementOf(swing, price) {
    const range = swingRange(swing);
    if (range <= 0)
        return 0;
    return swing.direction === 'up'
        ? (swing.end.price - price) / range
        : (price - swing.end.price) / range;
}
export function swingInvalidation(swing, bars) {
    const up = swing.direction === 'up';
    const startEdge = swing.start.extreme ?? swing.start.price;
    const endEdge = swing.end.extreme ?? swing.end.price;
    for (const bar of bars) {
        if (bar.time <= swing.end.time)
            continue;
        // Past the end: a new extreme in the swing's own direction.
        if (up ? bar.high > endEdge : bar.low < endEdge) {
            return { passed: 'end', time: bar.time, price: up ? bar.high : bar.low };
        }
        // Past the start: the retracement went the whole way and beyond.
        if (up ? bar.low < startEdge : bar.high > startEdge) {
            return { passed: 'start', time: bar.time, price: up ? bar.low : bar.high };
        }
    }
    return null;
}
/**
 * What counts as a reversal on each timeframe, as a share of price.
 *
 * The one rule that is still a number rather than a definition. A
 * counter-move smaller than this is a pullback inside the move; larger, and
 * the move has ended. Tuned by hand against the owner's draws so far and
 * meant to keep being tuned against them: percent of price rather than a
 * fixed amount so the same setting holds as gold moves from 2000 to 5000.
 */
export const FIB_REVERSAL_PCT = {
    // From the owner's verdicts of 4 Nov: on each chart the approved swings
    // were the larger ones and the small ones were denied ("very small, on
    // one 1m candle"), while a 3.6% swing on M1 was "too big, draw it on
    // bigger timeframes". So each chart carries its own band of sizes, each
    // a few times the one below.
    M1: 0.006,
    // 4% let the 3 Sep bounce inside the Aug→Oct decline start a swing of its
    // own; the owner denied that one and approved the whole decline.
    M15: 0.1,
    H1: 0.1,
    H4: 0.1,
    D1: 0.2,
};
/**
 * The scales a chart is read at, as multiples of its reversal size.
 *
 * The owner's own draws on one H4 chart span a 735-point rise (T4) and the
 * 1,400-point decline it sat inside (T6). No single reversal size draws
 * both, so each chart is read at more than one, smallest first, and every
 * swing still standing is drawn. Two, not four: the verdicts denied every
 * ×1 and ×18 of the old ladder, and the approved sizes on each chart sat
 * within a factor of three of each other.
 */
export const FIB_SCALES = {
    M1: [1, 2.5],
    M15: [1],
    // From H1 up, 2.5× the base was already the multi-year swing the owner
    // denied on every chart; the bigger swing belongs to the bigger chart.
    H1: [1],
    H4: [1],
    D1: [1],
};
/**
 * ZigZag pivots: alternating highs and lows, each confirmed once price has
 * moved away from it by at least `pct` of its price.
 *
 * The leg in progress is NOT emitted here. Its extreme is unconfirmed by
 * definition; `latestLeg` reports it separately as a running end.
 */
export function detectPivots(bars, pct) {
    const out = [];
    if (bars.length === 0 || pct <= 0)
        return out;
    let direction = null;
    let hiIndex = 0;
    let hiPrice = bars[0].high;
    let loIndex = 0;
    let loPrice = bars[0].low;
    for (let i = 1; i < bars.length; i++) {
        const bar = bars[i];
        if (direction !== 'down') {
            if (bar.high > hiPrice) {
                hiPrice = bar.high;
                hiIndex = i;
            }
            if (hiPrice - bar.low >= hiPrice * pct) {
                out.push({ time: bars[hiIndex].time, price: hiPrice, kind: 'high', confirmedTime: bar.time });
                direction = 'down';
                loPrice = bar.low;
                loIndex = i;
                continue;
            }
        }
        if (direction !== 'up') {
            if (bar.low < loPrice) {
                loPrice = bar.low;
                loIndex = i;
            }
            if (bar.high - loPrice >= loPrice * pct) {
                out.push({ time: bars[loIndex].time, price: loPrice, kind: 'low', confirmedTime: bar.time });
                direction = 'up';
                hiPrice = bar.high;
                hiIndex = i;
            }
        }
    }
    return out;
}
/**
 * The swing the rules point at right now.
 *
 * Normally the last completed leg: from the second-last confirmed pivot to
 * the last one (rule 4 — end = the last reversal, start = where the move into
 * it began). If price has since passed that leg's start, the leg is gone
 * (rule 5) and what is left is the move in progress: from the last pivot to
 * the extreme reached since, which keeps moving until a reversal confirms.
 */
export function latestLeg(bars, pct) {
    const pivots = detectPivots(bars, pct);
    const n = pivots.length;
    if (n === 0)
        return null;
    const last = pivots[n - 1];
    const runningLeg = () => {
        // From the last pivot to the extreme since it.
        const up = last.kind === 'low';
        let best = null;
        for (const bar of bars) {
            if (bar.time <= last.time)
                continue;
            if (best === null || (up ? bar.high > best.high : bar.low < best.low))
                best = bar;
        }
        const end = best
            ? { time: best.time, price: up ? best.high : best.low }
            : { time: last.time, price: last.price };
        return {
            swing: { direction: up ? 'up' : 'down', start: { time: last.time, price: last.price }, end },
            running: true,
            pivots,
        };
    };
    if (n < 2)
        return runningLeg();
    const prev = pivots[n - 2];
    const swing = {
        direction: last.kind === 'high' ? 'up' : 'down',
        start: { time: prev.time, price: prev.price },
        end: { time: last.time, price: last.price },
    };
    // Passed its start since? Then the retracement became the next move.
    const gone = swingInvalidation(swing, bars);
    if (gone?.passed === 'start')
        return runningLeg();
    return { swing, running: false, pivots };
}
/**
 * The confirmation point of an extreme, on finer bars (owner's rules 13–14,
 * refined by T4 and T6).
 *
 * For a top: after the extreme, the first pullback low is the low held
 * before the first bounce. The reversal is confirmed by the first bar that
 * CLOSES below it, and the point is the highest high of the pullback that
 * follows, before a close below the confirmation bar's low (or that bar's
 * own high when price runs at once).
 *
 * A top can break more than once before the move leaves — T6 broke on
 * 2 Mar, retested, and only left after the 3 Mar break. So the search walks
 * the staircase: each further DECISIVE break (a bar spanning at least half
 * the chart's reversal size) and its pullback replaces the candidate, until
 * a close sits a full reversal size below the candidate. If price instead
 * closes back ABOVE the candidate, that launch failed and the search starts
 * again from the high since. The first break always counts, whatever its
 * size, so a quiet top like T4 still resolves. Mirror for a bottom.
 *
 * Returns null when the structure has not completed inside `fine`.
 */
export function refineExtreme(fine, kind, extremeTime, extremeSpan, options = { movePct: 0.01 }) {
    const top = kind === 'high';
    // Generic accessors so one walk serves both directions: `far` is the price
    // in the move's direction, `near` against it.
    const far = (b) => (top ? b.low : b.high);
    const near = (b) => (top ? b.high : b.low);
    const beyond = (a, b) => (top ? a < b : a > b); // a further along the move than b
    const back = (a, b) => (top ? a > b : a < b);
    // The finer bar that made the extreme, inside the coarse bar's span.
    let i0 = -1;
    for (let i = 0; i < fine.length; i++) {
        const b = fine[i];
        if (b.time < extremeTime || b.time >= extremeTime + extremeSpan)
            continue;
        if (i0 === -1 || back(near(b), near(fine[i0])))
            i0 = i;
    }
    if (i0 === -1)
        return null;
    const extreme = near(fine[i0]);
    let candidate = null;
    let from = i0; // the bar the current pullback search starts after
    let prevEnd = i0; // last bar already examined for the leave/fail tests
    for (;;) {
        // First pullback after `from`: the far extreme held before the first counter-bar.
        let pIdx = -1;
        let bounceIdx = -1;
        for (let j = from + 1; j < fine.length; j++) {
            const b = fine[j];
            if (pIdx === -1) {
                pIdx = j;
                continue;
            }
            if (back(near(b), near(fine[pIdx]))) {
                bounceIdx = j;
                break;
            }
            if (beyond(far(b), far(fine[pIdx])))
                pIdx = j;
        }
        if (bounceIdx === -1)
            return candidate;
        const pullback = far(fine[pIdx]);
        // Confirmation: the first close through the pullback.
        let cIdx = -1;
        for (let k = bounceIdx; k < fine.length; k++) {
            if (beyond(fine[k].close, pullback)) {
                cIdx = k;
                break;
            }
        }
        if (cIdx === -1)
            return candidate;
        const conf = fine[cIdx];
        // Between the last step and this break: did the move leave, or the launch fail?
        if (candidate !== null) {
            const away = options.movePct * candidate.price;
            let failed = -1;
            for (let n = prevEnd + 1; n <= cIdx; n++) {
                const c = fine[n].close;
                if (top ? c <= candidate.price - away : c >= candidate.price + away)
                    return candidate;
                if (back(c, candidate.price)) {
                    failed = n;
                    break;
                }
            }
            if (failed !== -1) {
                // The launch failed: search again from the extreme reached since.
                let hi = failed;
                for (let n = failed; n < fine.length; n++) {
                    const b = fine[n];
                    if (beyond(b.close, candidate.price))
                        break;
                    if (back(near(b), near(fine[hi])))
                        hi = n;
                }
                candidate = null;
                from = hi;
                prevEnd = hi;
                continue;
            }
        }
        // The pullback after the confirmation bar, until a close beyond it.
        let best = null;
        let wIdx = cIdx;
        for (let m = cIdx + 1; m < fine.length; m++) {
            const b = fine[m];
            if (beyond(b.close, far(conf)))
                break;
            if (best === null || back(near(b), near(best)))
                best = b;
            wIdx = m;
        }
        const point = best ?? conf;
        const decisive = candidate === null || conf.high - conf.low >= 0.5 * options.movePct * conf.close;
        if (decisive) {
            candidate = { time: point.time, price: near(point), extreme, confirmedTime: conf.time };
        }
        // Next step: the pullback window's own low is the next level a break must close through.
        from = cIdx;
        prevEnd = wIdx;
    }
}
//# sourceMappingURL=fibonacci.js.map